/**
 * Pure presentation for the display (non-editing) mode of a line-item
 * row. Handles the conflict icon, the pressable/plain text (switched
 * by `readOnly`), and the hover action buttons (suppressed when
 * `readOnly`).
 *
 * Extracted from {@link LineItemRowView} so the parent stays under the
 * project's cognitive-complexity threshold while still satisfying the
 * `single-component-per-file` rule.
 * @module features/contracts/components/LineItemDisplayRow/LineItemDisplayRowView
 */

import { AlertTriangle, Pencil, Trash2 } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import { Button } from "@/components/atoms/Button";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for {@link LineItemDisplayRowView}. */
export interface LineItemDisplayRowViewProps {
  /** Category key for testID namespacing. */
  readonly category: string;
  /** Item index for testID namespacing. */
  readonly index: number;
  /** Display text. */
  readonly text: string;
  /** Whether the item is flagged as a conflict. */
  readonly isConflict: boolean;
  /** Whether the item was just added / edited. */
  readonly isRecentlyModified: boolean;
  /** When `true`, renders text as plain and hides action buttons. */
  readonly readOnly: boolean;
  /** Pre-bound handler for entering edit mode. */
  readonly onPressEdit: () => void;
  /** Pre-bound handler for opening the delete confirmation. */
  readonly onPressDelete: () => void;
}

/**
 * Returns the className used for the row container based on conflict
 * and recently-modified state. Conflict styling wins over the blue
 * "modified" highlight when both apply.
 * @param isConflict - Whether the item is flagged as a conflict
 * @param isRecentlyModified - Whether the item was just added / edited
 * @returns NativeWind className for the row HStack
 */
function rowClassName(
  isConflict: boolean,
  isRecentlyModified: boolean
): string {
  if (isConflict) {
    return "items-center gap-2 rounded-sm bg-needs-semantic-warning-100 p-2";
  }
  if (isRecentlyModified) {
    return "items-center gap-2 rounded-sm bg-surface-info-subtle p-2";
  }
  return "items-center gap-2 pl-2";
}

/**
 * Returns the text-style className for conflict vs non-conflict
 * branches. The `text-typography-*` tokens auto-invert between light
 * and dark mode via the GlueStack CSS-variable palette, so no `dark:`
 * counter-override is needed (see SE-4599).
 * @param isConflict - Whether the item is flagged as a conflict
 * @returns NativeWind className for the text
 */
function textClassName(isConflict: boolean): string {
  return isConflict ? "text-content-primary" : "text-content-secondary";
}

/**
 * Returns the className for the hover action bar container. Web uses
 * opacity + pointer-events gating so the buttons appear only on hover
 * or focus; native shows them inline because there is no hover surface.
 * @returns NativeWind className for the action-bar HStack
 */
function actionBarClassName(): string {
  return Platform.OS === "web"
    ? "pointer-events-none items-center gap-1 opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
    : "items-center gap-1";
}

/**
 * Renders the display-mode row.
 * @param props - Component props
 * @param props.category - Category key for testID namespacing.
 * @param props.index - Item index for testID namespacing.
 * @param props.text - Display text.
 * @param props.isConflict - Whether the item is flagged as a conflict.
 * @param props.isRecentlyModified - Whether the item was just added / edited.
 * @param props.readOnly - When `true`, renders text as plain and hides action buttons.
 * @param props.onPressEdit - Pre-bound handler for entering edit mode.
 * @param props.onPressDelete - Pre-bound handler for opening the delete confirmation.
 * @returns The display-mode row
 */
const LineItemDisplayRowView = ({
  category,
  index,
  text,
  isConflict,
  isRecentlyModified,
  readOnly,
  onPressEdit,
  onPressDelete,
}: LineItemDisplayRowViewProps) => (
  <Stack
    direction="horizontal"
    className={rowClassName(isConflict, isRecentlyModified)}
  >
    {isConflict ? (
      <Box testID={`conflict-icon-${category}-${index}`}>
        <Icon
          as={AlertTriangle}
          size="sm"
          className="text-status-warning-strong"
        />
      </Box>
    ) : null}
    {readOnly ? (
      <Box testID={`line-item-text-${category}-${index}`} className="flex-1">
        <Text variant="body" className={textClassName(isConflict)}>
          {text}
        </Text>
      </Box>
    ) : (
      <Pressable
        testID={`line-item-text-${category}-${index}`}
        accessibilityRole="button"
        accessibilityLabel={`Edit ${text}`}
        onPress={onPressEdit}
        className="flex-1"
      >
        <Text variant="body" className={textClassName(isConflict)}>
          {text}
        </Text>
      </Pressable>
    )}
    {readOnly ? null : (
      <Stack direction="horizontal" className={actionBarClassName()}>
        <Button
          testID={`line-item-edit-${category}-${index}`}
          accessibilityLabel="Edit line item"
          size="xs"
          variant="link"
          tone="secondary"
          icon={Pencil}
          onPress={onPressEdit}
        />
        <Button
          testID={`line-item-delete-${category}-${index}`}
          accessibilityLabel="Delete line item"
          size="xs"
          variant="link"
          tone="danger"
          icon={Trash2}
          onPress={onPressDelete}
        />
      </Stack>
    )}
  </Stack>
);

LineItemDisplayRowView.displayName = "LineItemDisplayRowView";

export default memo(LineItemDisplayRowView);
